import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Datagrid,
  DateField,
  ListContextProvider,
  Loading,
  Pagination,
  TextField,
  useList,
  useRedirect,
  SortPayload,
  FunctionField,
  useRefresh,
  useRecordContext,
} from 'react-admin';
import { useParams } from 'react-router-dom';
import { AdminPromocodesResponseDto } from 'shared/api';
import { API_URL } from 'shared/constants/api-url';
import { LABELS } from 'shared/constants/labels';
import { useHttpClient } from 'shared/hooks/useHttpClient';
import { Add as AddIcon } from '@mui/icons-material';
import { 
  styled, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton 
} from '@mui/material';
import { AddPromocodesForm } from './add-promocode-form';
import CloseIcon from '@mui/icons-material/Close';

const ITEMS_PER_PAGE = 10;
const DEFAULT_SORT_FIELD = 'expireDate' as const;
const DEFAULT_SORT_ORDER = 'ASC' as const;

interface PromoCodeWithId {
  id: number;
  [key: string]: any;
}

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const AddButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
  },
}));

const PatientPromocodes = () => {
  const { userId } = useRecordContext();
  const refresh = useRefresh();
  const [modalOpen, setModalOpen] = useState(false);
  const params = useParams<{ id: string }>();
  const redirect = useRedirect();
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>(DEFAULT_SORT_FIELD);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(DEFAULT_SORT_ORDER);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);
  
  const handleAddSuccess = useCallback(() => {
    handleCloseModal();
    refresh();
  }, [refresh]);

  const apiUrl = useMemo(() => {
    return `${API_URL.getPromocodes}?limit=${ITEMS_PER_PAGE}&page=${page}&sort=${sortField}&order=${sortOrder}&userId=${userId}`;
  }, [page, sortField, sortOrder, userId]);

  const { isLoading, data } = useHttpClient<AdminPromocodesResponseDto>(
    apiUrl,
    { skip: !params.id }
  );

  const promoCodesWithId: PromoCodeWithId[] | undefined = useMemo(() => {
    return data?.data.map((item, index) => ({
      ...item,
      id: index,
    }));
  }, [data?.data]);

  const handleSortChange = useCallback((sort: SortPayload) => {
    if (sort) {
      setSortField(sort.field);
      setSortOrder(sort.order === 'ASC' ? 'ASC' : 'DESC');
    }
  }, []);

  const baseContext = useList({ 
    data: promoCodesWithId, 
    page, 
    perPage: ITEMS_PER_PAGE, 
    isLoading,
    sort: { field: sortField, order: sortOrder },
  });
  
  const promocodeListContext = useMemo(() => ({
    ...baseContext,
    total: data?.total ?? 0,
    onSortChange: handleSortChange,
  }), [baseContext, data?.total, handleSortChange]);

  useEffect(() => {
    if (!params.id) {
      redirect('/patients');
    }
  }, [params.id, redirect]);

  if (isLoading) {
    return <Loading />;
  }

  if (!data || !data.data.length) {
    return (
      <>
        <AddButton
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
        >
          Добавить промокод
        </AddButton>
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
        >
          <StyledDialogTitle>
            Добавление промокода
            <IconButton aria-label="close" onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </StyledDialogTitle>
          <DialogContent>
            <AddPromocodesForm onSuccess={handleAddSuccess} />
          </DialogContent>
        </Dialog>
        <div>Промокоды отсутствуют</div>
      </>
    );
  }

  return (
    <>
      <AddButton
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpenModal}
      >
        Добавить промокод
      </AddButton>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>
          Добавление промокода
          <IconButton aria-label="close" onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent>
          <AddPromocodesForm onSuccess={handleAddSuccess} />
        </DialogContent>
      </Dialog>
      <ListContextProvider value={promocodeListContext}>
        <Datagrid bulkActionButtons={false}>
          <TextField source="code" label={LABELS.promocodeCode} />
          <FunctionField
            label={LABELS.promocodeType}
            render={(record: { discountType: string }) => {
              const discountTypeMap: Record<string, string> = {
                'amount': 'номинал',
                'percentage': 'процент'
              };

              return discountTypeMap[record.discountType] || record.discountType;
            }}
          />
          <TextField source="amount" label={LABELS.promocodeAmount} sortable={true} />
          <TextField source="percentage" label={LABELS.promocodePercentage} sortable={true} />
          <DateField
            label={LABELS.promocodeDateUsage}
            source="usageDate"
            sortable={true}
          />
          <DateField
            sortable={true}
            source="expireDate"
            label={LABELS.promocodeExpireDate}
            locales="ru-RU"
            options={{ 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            }}
          />
          <DateField
            label={LABELS.createdAt}
            source="createdAt"
            sortable={true}
          />
        </Datagrid>
        <Pagination
          isLoading={isLoading}
          page={page}
          perPage={ITEMS_PER_PAGE}
          total={promocodeListContext.total}
          setPage={setPage}
        />
      </ListContextProvider>
    </>
  );
};

export default PatientPromocodes;
